import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { AdultsList, AgesList, ChildrenList } from 'ts/hotel/interfaces/SearchFilter';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import MovablePlaceholder from 'components/ui/MovablePlaceholder/MovablePlaceholder';
import { Select } from 'ts/interfaces/Generic';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useClickAway } from 'hooks/useClickAway';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getKey } from 'utils/hotel';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b } from 'constants/generic';

interface Room {
  adults: number;
  children: number[];
  id: number;
}

interface RoomsDropdownProps {
  onChange: (value: string) => void;
  adultsList?: AdultsList[];
  childrenList?: ChildrenList[];
  agesList?: AgesList[];
  movablePlaceholderIcon?: any;
  movablePlaceholder?: string;
}

const RoomsDropdown: FC<RoomsDropdownProps> = (props) => {
  const [rooms, setRooms] = useState<Room[]>([{ adults: 2, children: [], id: 0 }]);
  const [open, setOpen] = useState(false);
  const [adultsList, setAdultsList] = useState<Select[]>([]);
  const [childrenList, setChildrenList] = useState<Select[]>([]);
  const [agesList, setAgesList] = useState<Select[]>([]);
  const [urlParams] = useSearchParams();
  const [t] = useTranslation();
  const { hotelIndex } = useContext(HotelConfigContext);
  const { pathname } = useLocation();

  const ref = useClickAway(() => {
    setOpen(false);
  });

  const parseRoomsString = (value: string): Room[] =>
    value
      .split(';')
      .map((room) => room.trim())
      .filter(Boolean)
      .map((room, index) => {
        const [adultsRaw, ...childrenRaw] = room.split('_');
        const adults = Number.parseInt(adultsRaw, 10);
        return {
          adults: Number.isFinite(adults) && adults > 0 ? adults : 2,
          children: childrenRaw
            .map((item) => Number.parseInt(item, 10))
            .filter((age) => Number.isFinite(age) && age >= 0),
          id: index,
        };
      });

  useEffect(() => {
    if (!(pathname.includes(hotelIndex + '') || (!/\d/.test(pathname) && !hotelIndex))) {
      return;
    }

    const roomsString = urlParams.get('roomsString');
    if (roomsString) {
      const newRooms = parseRoomsString(roomsString);
      setRooms(newRooms.length ? newRooms : [{ adults: 2, children: [], id: 0 }]);
      return;
    }

    const roomsStringLocalSt = localStorage.getItem(getKey('roomsStringHotel', hotelIndex));
    if (roomsStringLocalSt) {
      const newRooms = parseRoomsString(roomsStringLocalSt);
      setRooms(newRooms.length ? newRooms : [{ adults: 2, children: [], id: 0 }]);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTimeout(() => {
      const newAdultsList =
        props.adultsList?.map((item) => ({
          value: parseFloat(item.value),
          label: item.text,
        })) || [];
      setAdultsList(newAdultsList);

      const newChildrenList =
        props.childrenList?.map((item) => ({
          value: parseFloat(item.value),
          label: item.text,
        })) || [];
      setChildrenList(newChildrenList);

      const newAgesList =
        props.agesList?.map((item) => ({
          value: parseFloat(item.value),
          label: item.text,
        })) || [];
      setAgesList(newAgesList);

      onChange(rooms);
    }, 100);
  }, [props.adultsList, props.childrenList, props.agesList]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (newRooms: Room[]) => {
    const roomsString = newRooms
      .map((room) => {
        const childrenString = room.children.map((child) => child.toString()).join('_');
        return `${room.adults}${childrenString ? '_' : ''}${childrenString}`;
      })
      .join(';');

    props.onChange(roomsString);
  };

  const onChangeAdults = (value: number, index: number) => {
    const newRooms = [...rooms];
    newRooms[index].adults = value;
    setRooms(newRooms);
    onChange(newRooms);
    saveRoomsString(newRooms);
  };

  const onChangeChildren = (value: number, index: number) => {
    const newRooms = [...rooms];
    newRooms[index].children = [];
    for (let i = 0; i < value; i++) {
      newRooms[index].children.push(2);
    }
    setRooms(newRooms);
    onChange(newRooms);
    saveRoomsString(newRooms);
  };

  const onChangeAge = (value: number, roomIndex: number, childIndex: number) => {
    const newRooms = [...rooms];
    newRooms[roomIndex].children[childIndex] = value;
    setRooms(newRooms);
    onChange(newRooms);
    saveRoomsString(newRooms);
  };

  const removeRoom = (roomIndex: number) => {
    const newRooms = [...rooms];
    newRooms.splice(roomIndex, 1);
    setRooms(newRooms);
    onChange(newRooms);
    saveRoomsString(newRooms);
  };

  const onClickAdd = () => {
    const newRooms = [...rooms, { adults: 2, children: [], id: rooms.length }];
    setRooms(newRooms);
    onChange(newRooms);
    saveRoomsString(newRooms);
  };

  const getGuestCount = () => {
    return rooms.reduce((acc, room) => acc + room.adults + room.children.length, 0);
  };

  const adultsSelectOptions = useMemo(
    () => Array.from({ length: 8 }, (_, i) => ({ value: i + 1, label: String(i + 1) })),
    []
  );
  const childrenSelectOptions = useMemo(
    () => Array.from({ length: 5 }, (_, i) => ({ value: i, label: String(i) })),
    []
  );

  const saveRoomsString = (newRooms: Room[]) => {
    const roomsString = newRooms
      .map((room) => {
        const childrenString = room.children.map((child) => child.toString()).join('_');
        return `${room.adults}${childrenString ? '_' : ''}${childrenString}`;
      })
      .join(';');
    localStorage.setItem(getKey('roomsStringHotel', hotelIndex), roomsString);
  };

  const maxRooms = b2b ? 5 : 3;

  return (
    <div className="position-relative">
      <div
        className={
          'room-dropdown-wr form-control display-flex' +
          (props.movablePlaceholder ? ' movable-placeholder-input' : '')
        }
        onClick={() => setOpen(true)}
      >
        <span className="text-bold">{getGuestCount()}</span>{' '}
        <span className=" ph-5">{t('passenger.guests')},</span>
        <span className="text-bold">{rooms.length}</span>{' '}
        <span className=" ph-5">{t('hotel.rooms')}</span>
      </div>
      {props.movablePlaceholder && (
        <MovablePlaceholder
          label={props.movablePlaceholder}
          showSmall={!!getGuestCount}
          icon={props.movablePlaceholderIcon}
        />
      )}
      {open && (
        <div className="room-dropdown" ref={ref}>
          {rooms.map((room, roomIndex) => (
            <div
              className={'room-row' + (roomIndex < rooms.length - 1 ? ' room-row-divider' : '')}
              key={room.id}
            >
              <div className="w100p flex-between mb-20">
                <span className="text-bold">{t('search.room')} {roomIndex + 1}</span>
                {!!roomIndex && (
                  <span className='btn-blue text-sm cur-poi' onClick={() => removeRoom(roomIndex)}>
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="icon mr-5"
                    />
                    {t('delete')}
                  </span>
                )}
              </div>
              <div className="w100p flex-between mb-10 align-center">
                <div className="text-bold text-sm">{t('search.adults')}</div>
                <div className="quantity-selector">
                  <button
                    className="quantity-btn"
                    onClick={() => {
                      if (room.adults > 1) {
                        onChangeAdults(room.adults - 1, roomIndex);
                      }
                    }}
                    disabled={room.adults <= 1}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>
                  <div className="quantity-select-wr" onClick={(e) => e.stopPropagation()}>
                    <SelectCustom
                      options={adultsSelectOptions}
                      value={Math.min(8, Math.max(1, room.adults))}
                      onChange={(value) => onChangeAdults(Number(value), roomIndex)}
                      menuPlacement={'auto'}
                      appendToBody
                    />
                  </div>
                  <button
                    className="quantity-btn"
                    onClick={() => {
                      const maxAdults = adultsList.length > 0 ? Math.max(...adultsList.map(a => Number(a.value))) : 10;
                      if (room.adults < maxAdults) {
                        onChangeAdults(room.adults + 1, roomIndex);
                      }
                    }}
                    disabled={room.adults >= (adultsList.length > 0 ? Math.max(...adultsList.map(a => Number(a.value))) : 10)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
              <div className="w100p flex-between align-center">
                <div className="text-bold text-sm">{t('search.children')}</div>
                <div className="quantity-selector">
                  <button
                    className="quantity-btn"
                    onClick={() => {
                      if (room.children.length > 0) {
                        onChangeChildren(room.children.length - 1, roomIndex);
                      }
                    }}
                    disabled={room.children.length <= 0}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>
                  <div className="quantity-select-wr" onClick={(e) => e.stopPropagation()}>
                    <SelectCustom
                      options={childrenSelectOptions}
                      value={Math.min(4, Math.max(0, room.children.length))}
                      onChange={(value) => onChangeChildren(Number(value), roomIndex)}
                      menuPlacement={'auto'}
                      appendToBody
                    />
                  </div>
                  <button
                    className="quantity-btn"
                    onClick={() => {
                      const maxChildren = childrenList.length > 0 ? Math.max(...childrenList.map(c => Number(c.value))) : 10;
                      if (room.children.length < maxChildren) {
                        onChangeChildren(room.children.length + 1, roomIndex);
                      }
                    }}
                    disabled={room.children.length >= (childrenList.length > 0 ? Math.max(...childrenList.map(c => Number(c.value))) : 10)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
              {!!room.children.length && (
                <div className="w100p mt-15">
                  <div className="text-bold text-sm mb-10">{t('search.age')}</div>
                  <div className="flex-row">
                    {room.children.map((child, childIndex) => (
                      <div className="flex-column mb-5 mr-5" key={childIndex}>
                        <SelectCustom
                          options={agesList}
                          value={child}
                          onChange={(value) => onChangeAge(value, roomIndex, childIndex)}
                          menuPlacement={'auto'}
                          disabled={child === 1}
                          appendToBody
                        />
                        <Checkbox
                          label="Infant"
                          className="mt-5 mr-5"
                          onChange={(check) => onChangeAge(check ? 1 : 2, roomIndex, childIndex)}
                          checked={child === 1}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="mt-10">
            <button className="btn btn-sm btn-primary mr-5" onClick={() => setOpen(false)}>
              {t('close')}
            </button>
            {rooms.length < maxRooms && (
              <button className="btn btn-sm btn-default" onClick={onClickAdd}>
                {t('search.add_room')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(RoomsDropdown, 'RoomsDropdown');
